import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollText, Info, CheckCircle, XCircle, Clock } from 'lucide-react';
import { adminApi } from '@/lib/api';
import WorkflowApprovalDialog from './WorkflowApprovalDialog';

const ROLE_LABELS = {
  company_admin: 'Administrador',
  compliance_officer: 'Compliance Officer',
  triage_manager: 'Gestor de Triagem',
  case_manager: 'Gestor de Denúncias',
  dpo: 'DPO',
};

const STATUS_STYLES = {
  PENDENTE: { icon: Clock, cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  APROVADO: { icon: CheckCircle, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  REJEITADO: { icon: XCircle, cls: 'text-red-700 bg-red-50 border-red-200' },
};

const WorkflowApprovalCard = ({ report, currentUserRole, onChanged }) => {
  const [approvals, setApprovals] = useState([]);
  const [config, setConfig] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getReportWorkflow(report.id);
      setApprovals(data.approvals || []);
      setConfig(data.workflow_config || null);
    } catch {
      // silently ignore — workflow may not be configured for this tenant
    } finally {
      setLoading(false);
    }
  }, [report.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!approvals || approvals.length === 0) return null;

  const pending = approvals.find((a) => a.status === 'PENDENTE');
  const stages = (config && config.stages) || [];
  const pendingStage = pending ? stages.find((s) => s.stage === pending.stage) : null;
  const allowedRoles = (pendingStage && pendingStage.roles) || [];
  const canDecide = pending && (
    allowedRoles.includes(currentUserRole) ||
    currentUserRole === 'company_admin' ||
    currentUserRole === 'super_admin'
  );

  return (
    <Card data-testid="workflow-approval-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ScrollText className="w-5 h-5 text-indigo-600" />
          Workflow de Aprovação
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {pending && (
          <Alert className="bg-amber-50 border-amber-200">
            <Info className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-sm">
              <div className="text-amber-900 font-medium">
                Etapa pendente: <span className="font-mono">{pendingStage?.name || pending.stage}</span>
              </div>
              <div className="text-amber-800 mt-1 text-xs">
                Pode decidir: {(allowedRoles || []).map((r) => ROLE_LABELS[r] || r).join(', ') || '—'}
                {' '}(Administrador e Super Admin sempre autorizados)
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Timeline of approvals */}
        <ul className="space-y-2" data-testid="workflow-approvals-list">
          {approvals.map((a) => {
            const S = STATUS_STYLES[a.status] || STATUS_STYLES.PENDENTE;
            const StageIcon = S.icon;
            const stageCfg = stages.find((s) => s.stage === a.stage);
            return (
              <li key={a.id} className={`rounded-md border p-3 ${S.cls}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 text-sm">
                    <StageIcon className="w-4 h-4" />
                    <span className="font-medium">{stageCfg?.name || a.stage}</span>
                    <Badge variant="outline" className="text-xs">{a.status}</Badge>
                  </div>
                  <span className="text-xs opacity-70">
                    {a.completed_at
                      ? new Date(a.completed_at).toLocaleString('pt-PT')
                      : new Date(a.created_at).toLocaleString('pt-PT')}
                  </span>
                </div>
                {a.comments && (
                  <div className="text-xs mt-2 whitespace-pre-wrap opacity-90">
                    <strong>Fundamentação:</strong> {a.comments}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {pending && (
          <div className="flex justify-end">
            <Button
              onClick={() => setDialogOpen(true)}
              disabled={!canDecide}
              title={canDecide ? undefined : 'O seu role não pode decidir esta etapa'}
              data-testid="open-workflow-decide-btn"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {canDecide ? 'Decidir etapa' : 'Sem permissão'}
            </Button>
          </div>
        )}
      </CardContent>

      {pending && (
        <WorkflowApprovalDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          report={report}
          stage={pendingStage || { stage: pending.stage, name: pending.stage }}
          allowedRoles={allowedRoles}
          onDecided={() => { load(); onChanged?.(); }}
        />
      )}
    </Card>
  );
};

export default WorkflowApprovalCard;
